import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import React, { useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { IconInput } from "@/core/input";
import { OutlineAccountIcon } from "@/icon/shared";

import { DataList13FData, DataList13FProps } from "./DataList13FProps";
import { getFilteredData } from "./utils";

export const DataList13F = ({
  data,
  lang = "zh",
  className,
  onSelect,
  selectedInvestor,
  ...props
}: DataList13FProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);

  const filteredData = useMemo(
    () => getFilteredData({ data, query, lang }),
    [data, lang, query]
  );
  const handleSelect = (investor: string) => {
    onSelect?.(investor);
    setIsOpen(false);
    setTimeout(() => {
      setQuery("");
    }, 200);
  };

  const stickToSelectedInvestor = () => {
    if (!selectedInvestor) return;
    const selectedElement = document.querySelector(
      `[data-investor="${selectedInvestor}"]`
    ) as HTMLDivElement;
    if (!selectedElement) return;

    const { top: selectedTop } = selectedElement.getBoundingClientRect();
    const { top: menuTop } = menuRef.current?.getBoundingClientRect() ?? {
      top: 0,
    };
    menuRef.current?.scrollBy({ top: selectedTop - menuTop });
    selectedElement.style.background = "#EEEEEE80";
  };

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <div {...props} className={twMerge("relative w-full", className)}>
        <IconInput
          placeholder={lang === "zh" ? "搜尋 13F 機構" : "13F institutions"}
          value={query}
          onFocus={() => {
            setIsOpen(true);
            stickToSelectedInvestor();
          }}
          aria-label="search 13F institution"
          onChange={(e) => setQuery(e.target.value)}
          leadingIcon={<MagnifyingGlassIcon className="h-5 w-5 text-white" />}
          className="bg-white bg-opacity-10 text-sm text-white placeholder-opacity-0 ring-0 placeholder:text-white placeholder:text-opacity-80"
        />
        <div
          role="list"
          ref={menuRef}
          style={
            {
              "--height":
                isOpen && filteredData.length
                  ? `${filteredData.length * 72}px`
                  : "0px",
            } as React.CSSProperties
          }
          className={twMerge(
            "absolute left-0 top-[100%] h-[var(--height)] max-h-[400px] w-full divide-y overflow-y-auto rounded-md bg-white shadow-md transition-all"
          )}
          data-testid="search-bar-popover"
        >
          {filteredData.map((item, index) => (
            <MenuItem
              key={index}
              data={item}
              lang={lang}
              onClick={handleSelect}
            />
          ))}
        </div>
      </div>
    </ClickAwayListener>
  );
};

type MenuItemProps = {
  data: DataList13FData;
  lang: "en" | "zh";
  onClick: (investor: string) => void;
};

const MenuItem = ({ data, lang, onClick }: MenuItemProps) => (
  <div
    role="listitem"
    data-investor={data.investor}
    className="flex h-[72px] cursor-pointer select-none flex-col gap-y-1 p-4 transition-colors hover:bg-sky-100"
    onClick={() => {
      if (data.investor) onClick(data.investor);
    }}
  >
    <div className="flex flex-wrap items-center justify-between overflow-hidden sm:justify-between">
      <p className="flex max-w-[300px] items-center gap-2 overflow-hidden whitespace-nowrap">
        <span className="text-sm font-medium">{data.investorName}</span>
        {lang === "zh" ? (
          <span className="truncate text-xs">{data.investorNameEn}</span>
        ) : null}
      </p>
      <p className="text-xs text-neutral-500">{data.updatedAt}</p>
    </div>
    <p className="flex items-center gap-x-2 text-xs">
      <OutlineAccountIcon />
      <span className="text-neutral-500">{data.representative}</span>
    </p>
  </div>
);
